import { css } from '@emotion/core';
import moment from 'moment';
import t from 'react-translate';
import { connect } from 'react-redux';
import { useState, useEffect, useContext } from 'react';
import { RootState } from 'redux/schemas';
import {
  getExportDetails,
  getExportData,
  getExportFileList,
  addDownloadFile,
} from 'redux/actions/license';
import {
  EnrollmentPeriod,
  ExportDetails,
  ExportTypes,
  ExportFile,
  ExportStatus,
} from 'redux/schemas/models/license';
import { getCurrentOrgCreationDate } from 'redux/selectors/licenses';
import {
  NvDropdown, NvDropdownButtonStyle, NvDropdownAlign, NvDropdownOption,
} from 'shared/components/inputs/nv-dropdown';
import PusherService from 'shared/services/pusher-service';
import { AngularServicesContext } from 'react-app';
import { halfSpacing } from 'styles/global_defaults/scaffolding';
import cloneDeep from 'lodash/cloneDeep';

type StateProps = {
  currentInstitutionId: number,
  orgCreationDate: moment.Moment,
  exportDetails: ExportDetails,
  exportFileList: ExportFile[],
};

type OwnProps = {
  periodType: EnrollmentPeriod,
  periodStart: moment.Moment,
  periodEnd: moment.Moment,
};

const actions = {
  getExportDetails,
  getExportData,
  getExportFileList,
  addDownloadFile,
};

const getExportTextForPeriod = (period: EnrollmentPeriod, date?: moment.Moment): string => {
  switch (period) {
    case EnrollmentPeriod.CURRENT_CONTRACT_PERIOD: return t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.CURRENT_CONTRACT_PERIOD();
    case EnrollmentPeriod.SINCE_ORG_CREATION: return date ? t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.ALL_DATA_SINCE(date.format('MM/DD/YYYY')) : t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.ALL_DATA_SINCE_DEFAULT();
    case EnrollmentPeriod.PAST_12_MONTHS: return t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.FROM_PAST_MONTHS(12);
    case EnrollmentPeriod.PAST_6_MONTHS: return t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.FROM_PAST_MONTHS(6);
    case EnrollmentPeriod.PAST_30_DAYS: return t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.FROM_PAST_DAYS(30);
    case EnrollmentPeriod.CUSTOM_DATE_RANGE: return t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.WITHIN_A_DATE_RANGE();
    default: return '';
  }
};

const getUserDataExportAlertHeaderText = (period: EnrollmentPeriod, startDate?: moment.Moment, endDate?: moment.Moment) => {
  switch (period) {
    case EnrollmentPeriod.CURRENT_CONTRACT_PERIOD:
      return t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.USER_DATA.SUCCESS.HEADER_CURRENT_CONTRACT_PERIOD();
    case EnrollmentPeriod.CUSTOM_DATE_RANGE:
      return t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.USER_DATA.SUCCESS.HEADER_DATE_RANGE(startDate.format('MM/DD/YYYY'), endDate.format('MM/DD/YYYY'));
    case EnrollmentPeriod.PAST_12_MONTHS:
      return t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.USER_DATA.SUCCESS.HEADER_PAST_MONTHS(12);
    case EnrollmentPeriod.PAST_30_DAYS:
      return t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.USER_DATA.SUCCESS.HEADER_PAST_DAYS(30);
    case EnrollmentPeriod.PAST_6_MONTHS:
      return t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.USER_DATA.SUCCESS.HEADER_PAST_MONTHS(6);
    case EnrollmentPeriod.SINCE_ORG_CREATION:
      return t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.USER_DATA.SUCCESS.HEADER_ORG_CREATION_DATE(startDate.format('MM/DD/YYYY'));
    default:
      return '';
  }
};

const getEnrollmentExportAlertHeaderText = (period: EnrollmentPeriod, startDate?: moment.Moment, endDate?: moment.Moment) => {
  switch (period) {
    case EnrollmentPeriod.CURRENT_CONTRACT_PERIOD:
      return t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.ENROLLMENT_DATA.SUCCESS.HEADER_CURRENT_CONTRACT_PERIOD();
    case EnrollmentPeriod.CUSTOM_DATE_RANGE:
      return t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.ENROLLMENT_DATA.SUCCESS.HEADER_DATE_RANGE(startDate.format('MM/DD/YYYY'), endDate.format('MM/DD/YYYY'));
    case EnrollmentPeriod.PAST_12_MONTHS:
      return t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.ENROLLMENT_DATA.SUCCESS.HEADER_PAST_MONTHS(12);
    case EnrollmentPeriod.PAST_30_DAYS:
      return t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.ENROLLMENT_DATA.SUCCESS.HEADER_PAST_DAYS(30);
    case EnrollmentPeriod.PAST_6_MONTHS:
      return t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.ENROLLMENT_DATA.SUCCESS.HEADER_PAST_MONTHS(6);
    case EnrollmentPeriod.SINCE_ORG_CREATION:
      return t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.ENROLLMENT_DATA.SUCCESS.HEADER_ORG_CREATION_DATE(startDate.format('MM/DD/YYYY'));
    default:
      return '';
  }
};

export const ExportButton = (props: StateProps & typeof actions & OwnProps) => {
  const { $uibModal } = useContext(AngularServicesContext);
  const { periodType, periodStart, periodEnd } = props;

  const styles = css`
    &.nv-dropdown-container {
      margin-top: ${halfSpacing}px;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: flex-start;
    }
  `;

  const [exportOptions, setExportOptions] = useState([]);

  const loadDetails = (currentInstitutionId) => {
    if (currentInstitutionId) {
      props.getExportDetails({
        institutionId: currentInstitutionId,
      });
    }
  };

  const isExportOptionStatusActive = (exportType: string) => props.exportDetails[exportType][periodType].reportStatus === ExportStatus.IN_PROGRESS;

  useEffect(() => {
    const pusherChannel = PusherService.setupChannel(`public-institution-${props.currentInstitutionId}`);
    pusherChannel.bind('institution_report_event_name', (file) => {
      loadDetails(props.currentInstitutionId);
    });

    loadDetails(props.currentInstitutionId);

    return () => {
      pusherChannel.unbind('institution_report_event_name');
    };
  }, [props.currentInstitutionId]);

  /* Request a new list of export data files whenever the period is changed or the export status on both relevant exports
   * is updated. This updates the visual indicators of whether an export is happening and whether exports are available */
  useEffect(() => {
    if (!props.currentInstitutionId) {
      return;
    }

    props.getExportFileList({ institutionId: props.currentInstitutionId, period: periodType });
  }, [
    props.currentInstitutionId,
    periodType,
    props.exportDetails[ExportTypes.ORGANIZATION_ENROLLMENT][periodType],
    props.exportDetails[ExportTypes.ORGANIZATION_USERS][periodType],
  ]);

  useEffect(() => {
    let options: NvDropdownOption[] = [
      {
        type: 'text',
        text: t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.USER_DATA[isExportOptionStatusActive(ExportTypes.ORGANIZATION_USERS) ? 'ACTIVE' : 'READY'](),
        callback: () => props.getExportData({
          institutionId: props.currentInstitutionId,
          exportType: ExportTypes.ORGANIZATION_USERS,
          period: periodType,
          startTime: periodStart.toISOString(),
          endTime: periodEnd.toISOString(),
          alertSuccessHeader: getUserDataExportAlertHeaderText(periodType, periodStart, periodEnd),
        }),
        disabled: isExportOptionStatusActive(ExportTypes.ORGANIZATION_USERS),
      },
      {
        type: 'text',
        text: t.INSTITUTIONS.LICENSES.DASHBOARD.EXPORT.ENROLLMENT_DATA[isExportOptionStatusActive(ExportTypes.ORGANIZATION_ENROLLMENT) ? 'ACTIVE' : 'READY'](),
        callback: () => props.getExportData({
          institutionId: props.currentInstitutionId,
          exportType: ExportTypes.ORGANIZATION_ENROLLMENT,
          period: periodType,
          startTime: periodStart.toISOString(),
          endTime: periodEnd.toISOString(),
          alertSuccessHeader: getEnrollmentExportAlertHeaderText(periodType, periodStart, periodEnd),
        }),
        disabled: isExportOptionStatusActive(ExportTypes.ORGANIZATION_ENROLLMENT),
      },
    ];

    if (props.exportFileList.length) {
      options = options.concat(
        { type: 'divider' },
        {
          type: 'text',
          text: t.INSTITUTIONS.LICENSES.DASHBOARD.PREVIOUS_DATA_EXPORTS(),
          callback: () => $uibModal.open({
            backdrop: 'static',
            templateUrl: 'institutions/templates/data-exports-modal.html',
            controller: 'DataExportsModalController as vm',
            windowClass: 'full-screen-modal-handheld full-screen-modal-tablet tall-modal data-exports-modal',
            resolve: {
              institutionId: props.currentInstitutionId,
              periodType: () => periodType,
              // Pass a copy of the file list as this one is managed by reduxjs and not mutable
              exportFileList: () => cloneDeep(props.exportFileList),
            },
          }),
        },
      );
    }
    setExportOptions(options);
  }, [props.exportDetails, props.currentInstitutionId, periodType, props.periodStart, props.periodEnd, props.exportFileList]);

  return (
    <div css={styles} className='nv-dropdown-container'>
      <NvDropdown
        title={getExportTextForPeriod(periodType, props.orgCreationDate)}
        iconClass='icon-export'
        buttonStyle={NvDropdownButtonStyle.BUTTON}
        items={exportOptions}
        align={NvDropdownAlign.CENTER}
      />
    </div>
  );
};

export default connect<StateProps, typeof actions, OwnProps, RootState>(
  (state, ownProps) => ({
    currentInstitutionId: state.app.currentInstitutionId,
    orgCreationDate: getCurrentOrgCreationDate(state) ?? moment(),
    exportDetails: state.models.license.exportDetails,
    exportFileList: state.models.license.exportFileList,
    ...ownProps,
  }), actions as any,
)(ExportButton);
